import { Injectable } from '@angular/core';
import { CaeItem } from '@project/cae/cae-data.service';
import { ArticleVO } from '@article/model/vo/article.vo';
import { CaeProjectHelper } from '@project/processor/cae/cae-project-helper';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Connection } from '@3d-scene/model3d/connection';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { Log } from '@shared/debug/log';
import { CaeImportHelperService } from './cae-import-helper.service';
import { ArticleUtils } from '@article/util/article-utils';
import { Box3, Vector3 } from 'three';
import { ArrayUtils } from '@shared/util/array-utils';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ArticleCreationManagerFactory } from '@project/components/article/manager/article-creation.manager.factory';
import { ArticleCreationManager } from '@project/components/article/manager/article-creation.manager';
import { CaeImportErrorCollector } from '../cae-import-error-collector';
import { CaeRailsData } from './cae-rails-data';

@Injectable({
  providedIn: 'root',
})
export class CaeImportAccessoryService {

  private caeImportErrorCollector!: CaeImportErrorCollector;
  private articleCreationManager!: ArticleCreationManager;

  constructor(
    private caeProjectHelper: CaeProjectHelper,
    private articleCreationManagerFactory: ArticleCreationManagerFactory,
    private caeImportHelperService: CaeImportHelperService,
  ) {
  }

  public async addAccessory(caeRailsData: CaeRailsData, caeImportErrorCollector: CaeImportErrorCollector): Promise<void> {
    this.caeImportErrorCollector = caeImportErrorCollector;
    this.articleCreationManager = this.articleCreationManagerFactory.getNewInstance();
    this.articleCreationManager.showFaultMessage = false;
    for (const caeRail of caeRailsData.currentRails) {
      this.caeImportErrorCollector.processRail(caeRail);
      await this.addAccessoryOfItems(caeRail.items);
    }
  }

  private async addAccessoryOfItems(caeItems: CaeItem[]): Promise<void> {

    for (const caeItem of caeItems) {
      await this.addAccessoryOfItem(caeItem);
    }

    const accessoryCaeItems = caeItems.flatMap((caeItem) => caeItem.items);
    if (accessoryCaeItems.length > 0) {
      await this.addAccessoryOfItems(accessoryCaeItems);
    }
  }

  private async addAccessoryOfItem(caeItem: CaeItem): Promise<void> {
    for (const accessoryCaeItem of caeItem.items) {
      const article: ArticleVO | undefined = this.caeProjectHelper.getArticleByCaeBaseArticle(accessoryCaeItem);
      if (article) {
        this.articleCreationManager.clean();
        await this.attachAccessoryToArticle(caeItem, accessoryCaeItem, article);
      } else {
        this.caeImportErrorCollector.addErrorForItem(accessoryCaeItem, 'caeimport.accessory.error.no_article_found');
      }
    }
  }

  private async attachAccessoryToArticle(caeArticleItem: CaeItem, caeAccessoryItem: CaeItem, accessoryArticle: ArticleVO): Promise<void> {

    const articleContainers: ArticleContainer[] = this.caeImportHelperService.getArticleContainersForProjectArticle(caeArticleItem);

    if (articleContainers.length === 0 || !this.isNormalCaeAccessoryArticle(accessoryArticle)) {
      return;
    }

    const freeAcceptingCubes: Connection[] = ConnectionInfoUtils.findSuitableAcceptingConnections(accessoryArticle, articleContainers);
    const filteredAcceptingCubes: Connection[] = this.filterAcceptingCubesWithBestPosition(freeAcceptingCubes, caeAccessoryItem, articleContainers[0].bounds);
    if (filteredAcceptingCubes.length === 0) {
      this.accessoryArticleInsertFailed(caeAccessoryItem);
      return;
    }

    try {
      await this.articleCreationManager.createArticlesByAccessoryArticleAndAcceptingCubes(accessoryArticle, filteredAcceptingCubes);
      this.accessoryArticleInserted(caeAccessoryItem);
    } catch (error) {
      Log.error('Could not attach accessory to article: ', error);
      this.accessoryArticleInsertFailed(caeAccessoryItem);
    }
  }

  private isNormalCaeAccessoryArticle(accessoryArticle: ArticleVO): boolean {
    return (
      accessoryArticle &&
      !ArticleUtils.isLabelingArticle(accessoryArticle) &&
      !ArticleUtils.isBridge(accessoryArticle) &&
      !ArticleUtils.isWireBridgeArticle(accessoryArticle) &&
      !ArticleUtils.isEndPlateArticle(accessoryArticle) &&
      !ArticleUtils.isEndStopArticle(accessoryArticle)
    );
  }

  private filterAcceptingCubesWithBestPosition(acceptingCubes: Connection[], accessory: CaeItem, articleBounds: Box3): Connection[] {
    if (ArrayUtils.isEmpty(acceptingCubes)) {
      return [];
    }
    let nextAcceptingCube: Connection | undefined = this.findNextAcceptingToAccessoryOffset(acceptingCubes, accessory, articleBounds);
    if (!nextAcceptingCube) {
      nextAcceptingCube = this.findMostNorthernAccepting(acceptingCubes);
    }
    const suitableAcceptingCubes: Connection[] = this.findAllConnectionsWithSameXAxis(acceptingCubes, nextAcceptingCube);
    return suitableAcceptingCubes;
  }

  private findNextAcceptingToAccessoryOffset(acceptingCubes: Connection[], accessory: CaeItem, articleBounds: Box3): Connection | undefined {
    if (!accessory.offset.x || !accessory.offset.y || !accessory.offset.z) {
      return undefined;
    }

    let nextAcceptingCube: Connection | undefined = undefined;
    const targetPoint = new Vector3(articleBounds.max.x + accessory.offset.y * -10, accessory.offset.z * 10, accessory.offset.x * -10);
    // find next to Point
    let minDistance = 10000;
    for (const connection of acceptingCubes) {
      const distance = MathUtils.calculateMiddlePointByRectangle(connection.localRectangle).distanceTo(targetPoint);
      if (minDistance > distance) {
        minDistance = distance;
        nextAcceptingCube = connection;
      }
    }
    return nextAcceptingCube;
  }

  private findMostNorthernAccepting(acceptingCubes: Connection[]): Connection | undefined {
    let northAcceptingCube: Connection | undefined = undefined;
    const mostNorth = -1000000;
    // find most northern
    for (const connection of acceptingCubes) {
      const center = MathUtils.calculateMiddlePointByRectangle(connection.globalRectangle);
      if (center.y > mostNorth) {
        northAcceptingCube = connection;
      }
    }
    return northAcceptingCube;
  }

  private findAllConnectionsWithSameXAxis(acceptingCubes: Connection[], targetAcceptingCube: Connection): Connection[] {
    const suitableAcceptingCubes: Connection[] = [];
    const centerTarget = MathUtils.calculateMiddlePointByRectangle(targetAcceptingCube.globalRectangle);
    // Find all on X-Axis
    for (const connection of acceptingCubes) {
      const center = MathUtils.calculateMiddlePointByRectangle(connection.globalRectangle);
      if (Math.abs(centerTarget.y - center.y) < 1 && Math.abs(centerTarget.z - center.z) < 1) {
        suitableAcceptingCubes.push(connection);
      }
    }
    return suitableAcceptingCubes;
  }

  private accessoryArticleInserted(caeAccessoryItem: CaeItem): void {
    const createdContainers: ArticleContainer[] = this.articleCreationManager.getCreatedArticleContainers();
    if (createdContainers.length > 0) {
      const containerToProcess: ArticleContainer = createdContainers[0];
      const isDynamicLabelingArticle: boolean = ArticleUtils.isDynamicLabelingArticle(containerToProcess.article);
      if (!isDynamicLabelingArticle) {
        this.caeImportHelperService.replaceArticleGuid(containerToProcess.articleUsage, caeAccessoryItem.id);
      }

      for (const container of createdContainers) {
        this.caeImportHelperService.writeArticleNoWithPrefixIntoUsage(container.articleUsage, caeAccessoryItem.articleNo);
      }
    } else {
      this.accessoryArticleInsertFailed(caeAccessoryItem);
    }
  }

  private accessoryArticleInsertFailed(caeAccessoryItem: CaeItem): void {
    this.caeImportErrorCollector.addErrorForItem(caeAccessoryItem, 'caeImport.accessory.error.add_article_failed');
  }
}
